import { FormattedMessage, useIntl } from 'react-intl'
import { Text } from '@contrast-security-inc/design-system-typography'
import { css } from '@emotion/css'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import {
  Card,
  InformationPanel
} from '@contrast-security-inc/design-system-surfaces'
import {
  Tab,
  TabList,
  TabPanel,
  TabProvider
} from '@contrast-security-inc/design-system-navigations'
import React, { useState } from 'react'
import { getSeverityColor } from '../../sca/libraries/components/drilldown-panel/helpers'
import { RuleSeverity } from '../shared'
import { Badge } from '@contrast-security-inc/design-system-badges'
import {
  IconCaretDown,
  IconCaretUp,
  IconWarning
} from '@contrast-security-inc/design-system-foundations'
import { styles } from './styles'
import { getColorBySeverity, getApps, FindCvssReturnType } from './cveUtils'
import { OrgImpactPanel, VulnerabilityReport } from './cve-interfaces'

interface Props {
  vulnerability: VulnerabilityReport
  isOpen: boolean
  severityCode: string
  cvss: FindCvssReturnType
}

export function CveDetailBody({
  vulnerability,
  severityCode,
  isOpen,
  cvss
}: Props) {
  const [selectedTab, setSelectedTab] = React.useState<OrgImpactPanel>(
    OrgImpactPanel.Applications
  )

  const intl = useIntl()
  const [isOpenState, setIsOpenState] = useState(isOpen)

  const toggleIsOpen = () => {
    setIsOpenState(!isOpenState)
  }

  const baseScore = cvss.cvssv3 ? vulnerability?.cve?.cvssv3?.baseScore : null
  return (
    <div>
      <div>
        <Text
          as="div"
          className={styles.severityAndMetricsTitle}
          font="titleH5"
        >
          {intl.formatMessage({
            id: 'title_SeverityAndMetrics'
          })}
        </Text>

        {cvss.cvssv3 ? (
          <div className={styles.severityAndMetrics}>
            <Stack>
              <Stack>
                <Text as="div" className={styles.cvssTitle}>
                  {intl.formatMessage({
                    id: 'title_cvss3'
                  })}
                </Text>
              </Stack>

              <div
                className={styles.libraryOverviewCVEScore(
                  getSeverityColor(severityCode as RuleSeverity)
                )}
              >
                <Badge
                  color={getColorBySeverity(severityCode.toUpperCase())}
                  variant="Normal"
                  className={styles.badgeStyle1s}
                >
                  {Number(baseScore).toFixed(1)}
                </Badge>
              </div>
            </Stack>

            <Stack>
              <Stack>
                <Text as="div" className={styles.impactScore}>
                  {intl.formatMessage({
                    id: 'title_impactScore'
                  })}
                </Text>
              </Stack>
              <Badge
                color="neutral40"
                variant="Normal"
                className={styles.badgeStyle1s}
              >
                <Text font="body">
                  {vulnerability?.cve?.cvssv3?.impactSubscore}
                </Text>
              </Badge>
            </Stack>

            <Stack>
              <Stack>
                <Text as="div" className={styles.exploitabilityScore}>
                  {intl.formatMessage({
                    id: 'title_ExploitabilityScore'
                  })}
                </Text>
              </Stack>
              <Badge
                color="neutral40"
                variant="Normal"
                className={styles.badgeStyle1s}
              >
                <Text font="body">
                  {vulnerability?.cve?.cvssv3?.exploitabilitySubscore}
                </Text>
              </Badge>
            </Stack>
          </div>
        ) : null}
        <div>
          <Stack
            className={css`
              max-height: 10px;
            `}
          >
            <Text as="div" className={styles.epss}>
              {intl.formatMessage({
                id: 'title_Epss'
              })}
            </Text>
          </Stack>
          <Stack>
            <Badge
              color="neutral40"
              variant="Normal"
              className={styles.badgeStyle1s}
              style={{ marginLeft: '105px', marginTop: '-12px' }}
            >
              <Text font="body">{vulnerability.cve.epssScore}</Text>
            </Badge>
          </Stack>
        </div>
      </div>

      {cvss.cvssv3 || cvss.cvssv2 ? (
        <>
          <Text as="div" className={styles.vectorFormat} font="titleH5">
            {intl.formatMessage({
              id: 'title_vector'
            })}
          </Text>
          {cvss.cvssv2 ? (
            <span>
              <Card className={styles.vectorCard} data-testid={'cvssv2'}>
                <div className={styles.vectorGridCVSS}>
                  <Stack flexDirection="column">
                    <Text as="div" className={styles.vectorGridTitle}>
                      <div>
                        {intl.formatMessage({
                          id: 'title_AccessVector'
                        })}
                      </div>
                    </Text>
                    <Stack className={styles.panelData}>
                      <div>
                        {vulnerability?.cve?.cvssv2?.accessVector?.toLowerCase()}
                      </div>
                    </Stack>
                  </Stack>

                  <Stack flexDirection="column">
                    <Text as="div" className={styles.vectorGridTitle}>
                      <div>
                        {intl.formatMessage({
                          id: 'title_AccessComplexity'
                        })}
                      </div>
                    </Text>
                    <Stack className={styles.panelData}>
                      <div>
                        {vulnerability?.cve?.cvssv2?.accessComplexity?.toLowerCase()}
                      </div>
                    </Stack>
                  </Stack>

                  <Stack flexDirection="column">
                    <Text as="div" className={styles.vectorGridTitle}>
                      <div>
                        {intl.formatMessage({
                          id: 'title_authentication'
                        })}
                      </div>
                    </Text>
                    <Stack className={styles.panelData}>
                      <div>
                        {vulnerability?.cve?.cvssv2?.authentication?.toLowerCase()}
                      </div>
                    </Stack>
                  </Stack>

                  <Stack flexDirection="column">
                    <Text as="div" className={styles.vectorGridTitle}>
                      <div>
                        {intl.formatMessage({
                          id: 'title_Confidentiality'
                        })}
                      </div>
                    </Text>
                    <Stack className={styles.panelData}>
                      <div>
                        {vulnerability?.cve?.cvssv2?.confidentialityImpact?.toLowerCase()}
                      </div>
                    </Stack>
                  </Stack>

                  <Stack flexDirection="column">
                    <Text as="div" className={styles.vectorGridTitle}>
                      <div>
                        {intl.formatMessage({
                          id: 'title_Integrity'
                        })}
                      </div>
                    </Text>
                    <Stack className={styles.panelData}>
                      <div>
                        {vulnerability?.cve?.cvssv2?.integrityImpact?.toLowerCase()}
                      </div>
                    </Stack>
                  </Stack>

                  <Stack flexDirection="column">
                    <Text as="div" className={styles.vectorGridTitle}>
                      <div>
                        {intl.formatMessage({
                          id: 'title_Availability'
                        })}
                      </div>
                    </Text>
                    <Stack className={styles.panelData}>
                      <div>
                        {vulnerability?.cve?.cvssv2?.availabilityImpact?.toLowerCase()}
                      </div>
                    </Stack>
                  </Stack>
                </div>
              </Card>
            </span>
          ) : (
            <div data-testid={'cvssv3'}>
              <div>{vulnerability.cve?.cvssv3?.vector}</div>
              <div>
                <span>
                  <a
                    className="no-underline text-smaller"
                    onClick={() => toggleIsOpen()}
                  >
                    {isOpenState && (
                      <>
                        <span>
                          <Card className={styles.vectorCard}>
                            <div className={styles.vectorGridCVSS}>
                              <Stack flexDirection="column">
                                <Text
                                  as="div"
                                  className={styles.vectorGridTitle}
                                >
                                  <div>
                                    {intl.formatMessage({
                                      id: 'title_AttackVector'
                                    })}
                                  </div>
                                </Text>
                                <Stack className={styles.panelData}>
                                  <div>
                                    {vulnerability?.cve?.cvssv3?.attackVector?.toLowerCase()}
                                  </div>
                                </Stack>
                              </Stack>

                              <Stack flexDirection="column">
                                <Text
                                  as="div"
                                  className={styles.vectorGridTitle}
                                >
                                  <div>
                                    {intl.formatMessage({
                                      id: 'title_AttackComplexity'
                                    })}
                                  </div>
                                </Text>
                                <Stack className={styles.panelData}>
                                  <div>
                                    {vulnerability?.cve?.cvssv3?.attackComplexity?.toLowerCase()}
                                  </div>
                                </Stack>
                              </Stack>

                              <Stack flexDirection="column">
                                <Text
                                  as="div"
                                  className={styles.vectorGridTitle}
                                >
                                  <div>
                                    {intl.formatMessage({
                                      id: 'title_PrivilegesRequired'
                                    })}
                                  </div>
                                </Text>
                                <Stack className={styles.panelData}>
                                  <div>
                                    {vulnerability?.cve?.cvssv3?.privilegesRequired?.toLowerCase()}
                                  </div>
                                </Stack>
                              </Stack>

                              <Stack flexDirection="column">
                                <Text
                                  as="div"
                                  className={styles.vectorGridTitle}
                                >
                                  <div>
                                    {intl.formatMessage({
                                      id: 'title_UserInteraction'
                                    })}
                                  </div>
                                </Text>
                                <Stack className={styles.panelData}>
                                  <div>
                                    {vulnerability?.cve?.cvssv3?.userInteraction?.toLowerCase()}
                                  </div>
                                </Stack>
                              </Stack>

                              <Stack flexDirection="column">
                                <Text
                                  as="div"
                                  className={styles.vectorGridTitle}
                                >
                                  <div>
                                    {intl.formatMessage({
                                      id: 'title_Scope'
                                    })}
                                  </div>
                                </Text>
                                <Stack className={styles.panelData}>
                                  <div>
                                    {vulnerability?.cve?.cvssv3?.scope?.toLowerCase()}
                                  </div>
                                </Stack>
                              </Stack>

                              <Stack flexDirection="column">
                                <Text
                                  as="div"
                                  className={styles.vectorGridTitle}
                                >
                                  <div>
                                    {intl.formatMessage({
                                      id: 'title_Confidentiality'
                                    })}
                                  </div>
                                </Text>
                                <Stack className={styles.panelData}>
                                  <div>
                                    {vulnerability?.cve?.cvssv3?.confidentialityImpact?.toLowerCase()}
                                  </div>
                                </Stack>
                              </Stack>

                              <Stack flexDirection="column">
                                <Text
                                  as="div"
                                  className={styles.vectorGridTitle}
                                >
                                  <div>
                                    {intl.formatMessage({
                                      id: 'title_Integrity'
                                    })}
                                  </div>
                                </Text>
                                <Stack className={styles.panelData}>
                                  <div>
                                    {vulnerability?.cve?.cvssv3?.integrityImpact?.toLowerCase()}
                                  </div>
                                </Stack>
                              </Stack>

                              <Stack flexDirection="column">
                                <Text
                                  as="div"
                                  className={styles.vectorGridTitle}
                                >
                                  <div>
                                    {intl.formatMessage({
                                      id: 'title_Availability'
                                    })}
                                  </div>
                                </Text>
                                <Stack className={styles.panelData}>
                                  <div>
                                    {vulnerability?.cve?.cvssv3?.availabilityImpact?.toLowerCase()}
                                  </div>
                                </Stack>
                              </Stack>
                            </div>
                          </Card>
                        </span>
                        <div className={styles.linkCaret}>
                          <FormattedMessage id={'action_showLess'} />
                          <IconCaretUp
                            color="neutral50"
                            size="md"
                            className={styles.caret}
                          />
                        </div>
                      </>
                    )}

                    {!isOpenState && (
                      <div className={styles.linkCaret}>
                        <IconCaretDown
                          color="neutral50"
                          size="md"
                          className={styles.caret}
                        />
                        <span>
                          <FormattedMessage id={'action_showMore'} />
                        </span>
                      </div>
                    )}
                  </a>
                </span>
              </div>
            </div>
          )}
        </>
      ) : (
        <InformationPanel
          detail={intl.formatMessage({ id: 'cve_awaitingAnalysis' })}
          Icon={IconWarning}
          testId="error"
          className={styles.unknownSeverity}
        ></InformationPanel>
      )}

      {/*DESCRIPTION*/}
      <div>
        <Stack>
          <Text as="div" className={styles.vectorTitle} font="titleH5">
            <div>
              {intl.formatMessage({
                id: 'title_description'
              })}
            </div>
          </Text>
        </Stack>
        <div className={styles.descriptionBody}>
          {vulnerability.cve.description}
        </div>
      </div>

      {/* ORG IMPACT*/}
      <Text as="div" className={styles.orgImpactTitle} font="titleH5">
        {intl.formatMessage({
          id: 'title_OrgImpact'
        })}
      </Text>

      <div>
        <span>
          <Card className={styles.panelCard}>
            <React.Fragment key=".0">
              <div className={styles.gridPanelCard}>
                <Stack flexDirection="column">
                  <Text as="div">
                    <div>
                      {intl.formatMessage({
                        id: 'title_applications'
                      })}
                    </div>
                  </Text>
                  <Stack className={styles.panelData}>
                    <div className={styles.impactedStats1}>
                      {vulnerability.impactStats.impactedAppCount}
                    </div>{' '}
                    <div className={styles.serverCount}>
                      /{vulnerability.impactStats.totalAppCount}
                    </div>{' '}
                    <div className={styles.impactPercent}>
                      (
                      {Math.round(
                        vulnerability.impactStats.appPercentage * 100
                      ) / 100}
                      )%
                    </div>
                  </Stack>
                </Stack>

                <Stack flexDirection="column">
                  <Text as="div">
                    <div>
                      {intl.formatMessage({
                        id: 'title_servers'
                      })}
                    </div>
                  </Text>
                  <Stack className={styles.panelData}>
                    <div className={styles.impactedStats1}>
                      {vulnerability.impactStats.impactedServerCount}
                    </div>{' '}
                    <div className={styles.serverCount}>
                      /{vulnerability.impactStats.totalServerCount}
                    </div>{' '}
                    <div className={styles.impactPercent}>
                      (
                      {Math.round(
                        vulnerability.impactStats.serverPercentage * 100
                      ) / 100}
                      )%
                    </div>
                  </Stack>
                </Stack>
              </div>
            </React.Fragment>
          </Card>
        </span>
      </div>

      <div className={styles.tabProviderOrgPanel}>
        <TabProvider value={selectedTab}>
          <TabList
            onChange={(_, value) => setSelectedTab(value as OrgImpactPanel)}
          >
            <Tab label="Applications" value="applications" />
            <Tab label="Servers" value="servers" />
          </TabList>
          <TabPanel value="applications">
            <Stack flexDirection="column" className={styles.appColumnPanel}>
              {getApps(vulnerability.apps).map((app) => (
                <Stack key={app} className={styles.appPanel}>
                  {' '}
                  {app}{' '}
                </Stack>
              ))}
            </Stack>
          </TabPanel>
          <TabPanel value="servers">
            <Stack flexDirection="column" className={styles.appColumnPanel}>
              {getApps(vulnerability.servers).map((server) => (
                <Stack key={server} className={styles.appPanel}>
                  {' '}
                  {server}{' '}
                </Stack>
              ))}
            </Stack>
          </TabPanel>
        </TabProvider>
      </div>
    </div>
  )
}
